import React from 'react';
import { DatePicker, Input, Select, Alert } from 'antd';
import { FiAlertCircle, FiLock } from 'react-icons/fi';
import dayjs from 'dayjs';
import { BiometryInput } from '../BiometryInput/BiometryInput';
import { useTranslation } from 'react-i18next';

export const PregnancyForm = ({
  trimester,
  pregForm,
  setPregForm,
  isLmpDisabledByDate,
  isHistoricalExamDate = false,
  examDateLabel = '',
  isLocked,
  fieldErrors,
  uniqueDates,
  selectedDate,
  setSelectedDate,
  onBiometryChange,
  onPregnancyEditSource,
}: any) => {
  const { t } = useTranslation('NewCaseWizard');
  // --- EXCLUSIVE LOGIC ---
  const hasCRL = !!pregForm.crl && parseFloat(pregForm.crl) > 0;
  const hasOthers =
    (!!pregForm.bpd && parseFloat(pregForm.bpd) > 0) ||
    (!!pregForm.hc && parseFloat(pregForm.hc) > 0) ||
    (!!pregForm.ac && parseFloat(pregForm.ac) > 0) ||
    (!!pregForm.fl && parseFloat(pregForm.fl) > 0);
  const [showCharWarning, setShowCharWarning] = React.useState(false);

  // If CRL has value, lock others. If others have value, lock CRL.
  const disableCRL = isLocked || hasOthers;
  const disableOthers = isLocked || hasCRL;
  const disabledLmp = isLocked || isLmpDisabledByDate;
  const disabledEdd = isLocked || isLmpDisabledByDate;
 const handleGaChange = (field: 'gaWeeks' | 'gaDays', value: string) => {
  // Check if the user tried to type a non-numeric character
  if (/[^0-9]/.test(value)) {
    setShowCharWarning(true);
    // Hide the warning automatically after 3 seconds
    setTimeout(() => setShowCharWarning(false), 3000);
  }

  const numericValue = value.replace(/\D/g, '');
  onPregnancyEditSource?.('ga');
  setPregForm({ ...pregForm, [field]: numericValue });
};

  return (
    <div className="transition-opacity opacity-100">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold bg-pink-100 text-pink-600 dark:bg-pink-900 dark:text-pink-300">
            2
          </span>
          <h3 className="font-bold text-slate-700 dark:text-gray-200">
            {t('oneStep.pregnancy.title')} - {trimester}
          </h3>
        </div>
        {uniqueDates.length > 0 && (
          <Select
            size="small"
            value={selectedDate ? selectedDate.format('YYYYMMDD') : undefined}
            onChange={(v) => setSelectedDate(dayjs(v, 'YYYYMMDD'))}
            className="w-32 text-[10px]"
            options={uniqueDates.map((d: string) => ({
              label: dayjs(d, 'YYYYMMDD').format('DD/MM/YYYY'),
              value: d,
            }))}
            placeholder={t('oneStep.pregnancy.selectDate')}
            disabled={isLocked}
          />
        )}
      </div>

      {fieldErrors['pregnancy_global'] && (
        <Alert
          message={fieldErrors['pregnancy_global']}
          type="error"
          showIcon
          className="mb-4 text-xs font-medium"
        />
      )}
      {isLocked && (
        <div className="bg-pink-50 dark:bg-pink-900/20 p-2 text-xs text-pink-700 dark:text-pink-300 border border-pink-100 dark:border-pink-800 rounded mb-4 font-medium flex items-center gap-2">
          <FiAlertCircle /> {t('oneStep.pregnancy.locked')}
        </div>
      )}
      {isHistoricalExamDate && !isLocked && (
        <div className="bg-amber-50 dark:bg-amber-900/20 p-2.5 text-xs text-amber-800 dark:text-amber-200 border border-amber-100 dark:border-amber-800 rounded mb-4 font-medium flex items-start gap-2">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <span>
            {t('oneStep.pregnancy.historicalExamHint', {
              examDate: examDateLabel,
            })}
          </span>
        </div>
      )}

      <div className="space-y-4">
        {/* DATES */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-bold text-slate-400 dark:text-gray-500">
              {t('oneStep.pregnancy.lmp')}
            </label>
            <DatePicker
              format="DD/MM/YYYY"
              className={`w-full dark:bg-gray-700 dark:text-white ${!fieldErrors['pregnancy_dates'] ? 'dark:border-gray-600' : ''}`}
              value={pregForm.lmp}
              onChange={(d) => {
                onPregnancyEditSource?.('lmp');
                setPregForm({ ...pregForm, lmp: d });
              }}
              disabled={isLocked || disabledLmp}
              status={fieldErrors['pregnancy_dates'] ? 'error' : ''}
              style={
                fieldErrors['pregnancy_dates'] ? { borderColor: '#ef4444' } : {}
              }
            />
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-400 dark:text-gray-500">
              {t('oneStep.pregnancy.edd')}
            </label>
            <DatePicker
              format="DD/MM/YYYY"
              className={`w-full dark:bg-gray-700 dark:text-white ${!fieldErrors['pregnancy_dates'] ? 'dark:border-gray-600' : ''}`}
              value={pregForm.edd}
              onChange={(d) => {
                onPregnancyEditSource?.('edd');
                setPregForm({ ...pregForm, edd: d });
              }}
              disabled={isLocked || disabledEdd}
              status={fieldErrors['pregnancy_dates'] ? 'error' : ''}
              style={
                fieldErrors['pregnancy_dates'] ? { borderColor: '#ef4444' } : {}
              }
            />
          </div>
        </div>

        {/* GA */}
        <div
          className={`bg-slate-50 dark:bg-gray-700/30 p-3 rounded border ${fieldErrors['pregnancy_dates'] ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700' : 'dark:border-gray-600'}`}
        >
          <div className="flex justify-between mb-1">
            <label className="text-[11px] font-bold text-slate-500 dark:text-gray-400">
              {t('oneStep.pregnancy.gestationalAge')}
            </label>
            <span className="text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 rounded">
              {trimester}
            </span>
              {showCharWarning && (
              <span className="text-[10px] text-red-500 font-medium animate-pulse">
                {t('oneStep.pregnancy.numbersOnly')}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              suffix="w"
              value={pregForm.gaWeeks}
              // FIX: Use numeric filter
              onChange={(e) => handleGaChange('gaWeeks', e.target.value)}
              className="text-center dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              disabled={isLocked}
              status={showCharWarning ? 'warning' : (fieldErrors['pregnancy_dates'] ? 'error' : '')}

            />
            <Input
              suffix="d"
              value={pregForm.gaDays}
              // FIX: Use numeric filter
              onChange={(e) => handleGaChange('gaDays', e.target.value)}
              className="text-center dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              disabled={isLocked}
              status={showCharWarning ? 'warning' : (fieldErrors['pregnancy_dates'] ? 'error' : '')}
            />
          </div>
        </div>

        {/* BIOMETRY */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-[11px] font-bold text-slate-400 dark:text-gray-500">
              {t('oneStep.pregnancy.biometry')}
            </label>
            {/* Status indicators */}
            {disableOthers && (
              <span className="text-[10px] text-amber-600 flex items-center">
                <FiLock className="mr-1" /> {t('oneStep.pregnancy.modeT1')}
              </span>
            )}
            {disableCRL && (
              <span className="text-[10px] text-blue-600 flex items-center">
                <FiLock className="mr-1" /> {t('oneStep.pregnancy.modeT2T3')}
              </span>
            )}
          </div>

          <div className="grid gap-3">
            {/* FIRST TRIMESTER (CRL) */}
            <div
              className={
                disableCRL ? 'opacity-50 pointer-events-none grayscale' : ''
              }
            >
              <BiometryInput
                type="CRL"
                value={pregForm.crl}
                onChange={(v: string) => onBiometryChange('CRL', v)}
                disabled={isLocked || disableCRL}
                serverGA={pregForm.serverGaCrl}
              />
            </div>

            {/* SECOND/THIRD TRIMESTER */}
            <div
              className={`grid grid-cols-2 gap-3 ${disableOthers ? 'opacity-50 pointer-events-none grayscale' : ''}`}
            >
              <BiometryInput
                type="BPD"
                value={pregForm.bpd}
                onChange={(v: string) => onBiometryChange('BPD', v)}
                disabled={isLocked || disableOthers}
                serverGA={pregForm.serverGaBpd}
              />
              <BiometryInput
                type="HC"
                value={pregForm.hc}
                onChange={(v: string) => onBiometryChange('HC', v)}
                disabled={isLocked || disableOthers}
                serverGA={pregForm.serverGaHc}
              />
              <BiometryInput
                type="AC"
                value={pregForm.ac}
                onChange={(v: string) => onBiometryChange('AC', v)}
                disabled={isLocked || disableOthers}
                serverGA={pregForm.serverGaAc}
              />
              <BiometryInput
                type="FL"
                value={pregForm.fl}
                onChange={(v: string) => onBiometryChange('FL', v)}
                disabled={isLocked || disableOthers}
                serverGA={pregForm.serverGaFl}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
